import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  listContacts,
  deleteContact,
  deleteContacts,
  getContactsCount,
  importContacts,
  uploadContacts,
} from "../../services/contacts.api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;

    if (!Array.isArray(contacts)) {
      console.warn("⚠️ LOAD_CONTACTS: Payload no es un array:", contacts);
      return state;
    }

    /** Paginación en servidor: cada respuesta sustituye la página visible (no acumular miles de filas en memoria). */
    return contacts
      .filter((c) => c && c.id)
      .map((c) => ({ ...c }));
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    
    // ✅ CORREGIR: Validar que contact existe y tiene id
    if (!contact || !contact.id) {
      console.warn("⚠️ UPDATE_CONTACTS: Contact inválido recibido:", contact);
      return state; // ✅ Retornar estado actual sin cambios
    }
    
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // ✅ Merge superficial: preserva campos sólo-en-listado (ej. tickets / última interacción).
      const updatedState = [...state];
      const oldContact = updatedState[contactIndex];
      updatedState[contactIndex] = { ...oldContact, ...contact };
      console.log("🔄 UPDATE_CONTACTS: Contacto actualizado en reducer:", {
        id: contact.id,
        name: contact.name,
        oldProfilePicUrl: oldContact?.profilePicUrl,
        newProfilePicUrl: contact.profilePicUrl,
        stateLength: updatedState.length
      });
      return updatedState;
    } else {
      console.log("➕ UPDATE_CONTACTS: Contacto nuevo agregado:", {
        id: contact.id,
        name: contact.name,
        profilePicUrl: contact.profilePicUrl
      });
      return [{ ...contact }, ...state]; // ✅ Spread para nueva referencia
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// D8.21.3 — Lógica (19 estados + useReducer + effects + handlers + API) de Contacts.
export default function useContacts() {
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  // ✅ Estados para paginación frontend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  /** Mismo Drawer que en vista ticket: perfil 360 + mismo guardado PUT profile-360. */
  const [profile360Open, setProfile360Open] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileUploadRef = useRef(null);
  const [, forceUpdate] = useState(0); // ✅ NUEVO: Estado para forzar re-render

  // Estados para selección múltiple
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionType, setActionType] = useState(""); // "delete", "export" o "import"
  const [contactCount, setContactCount] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importResultModalOpen, setImportResultModalOpen] = useState(false);
  /** Total de contactos que cumplen el filtro (servidor); no confundir con contacts.length (solo página actual). */
  const [totalCount, setTotalCount] = useState(0);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setSelectedContacts([]);
    setSelectAll(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await listContacts({
            searchParam,
            pageNumber: String(page + 1),
            limit: rowsPerPage
          });

          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts || [] });
          setTotalCount(typeof data.count === "number" ? data.count : 0);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, page, rowsPerPage]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ CORREGIR: Validar que socket existe antes de agregar listeners
    if (!socket) {
      console.warn("⚠️ Socket no disponible para companyId:", companyId);
      return;
    }

    const handleContactUpdate = (data) => {
      // ✅ CORREGIR: Validar que data existe y tiene la estructura esperada
      if (!data || !data.action) {
        console.warn("⚠️ Socket contact: Data inválida recibida:", data);
        return;
      }

      if (data.action === "update" || data.action === "create") {
        // ✅ CORREGIR: Validar que data.contact existe y tiene id
        if (data.contact && data.contact.id) {
          console.log("🔄 Socket contact: Actualizando contacto:", {
            id: data.contact.id,
            name: data.contact.name,
            profilePicUrl: data.contact.profilePicUrl,
            contactData: data.contact
          });
          dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
          // ✅ NUEVO: Forzar re-render después de actualizar el estado
          forceUpdate(prev => prev + 1);
          console.log("✅ Socket contact: Contacto actualizado en tiempo real:", data.contact.name);
        } else {
          console.warn("⚠️ Socket contact: Contact inválido recibido:", data.contact);
        }
      }

      if (data.action === "delete") {
        // ✅ CORREGIR: Validar que data.contactId existe
        if (data.contactId) {
          dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
          console.log("✅ Socket contact: Contacto eliminado en tiempo real:", data.contactId);
        } else {
          console.warn("⚠️ Socket contact: contactId no encontrado en data:", data);
        }
      }
    };

    socket.on(`company-${companyId}-contact`, handleContactUpdate);

    return () => {
      // ✅ CORREGIR: Solo remover el listener específico, no desconectar el socket
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-contact`, handleContactUpdate);
      }
    };
  }, [socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setProfile360Open(false);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedContactId(null);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setProfile360Open(true);
  };

  const handleCloseProfile360 = () => {
    setProfile360Open(false);
    setSelectedContactId(null);
  };

  const handleDeleteContact = async (contactId) => {
    setLoading(true);
    try {
      await deleteContact(contactId);
      
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      setTotalCount((c) => Math.max(0, c - 1));

      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
    setDeletingContact(null);
  };

  // Funciones para selección múltiple
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
      setSelectAll(true);
    }
  };

  const handleDeleteSelectedContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    setLoading(true);
    try {
      await deleteContacts(selectedContacts);
      
      // ✅ CORREGIR: Actualizar el estado local después de eliminar múltiples contactos
      const n = selectedContacts.length;
      selectedContacts.forEach(contactId => {
        dispatch({ type: "DELETE_CONTACT", payload: contactId });
      });
      setTotalCount((c) => Math.max(0, c - n));

      toast.success(i18n.t("contacts.toasts.deletedAll"));
      setSelectedContacts([]);
      setSelectAll(false);
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
  };

  const handleExportSelectedContacts = () => {
    if (selectedContacts.length === 0) return;
    
    // Filtrar solo los contactos seleccionados
    const contactsToExport = contacts.filter(contact => 
      selectedContacts.includes(contact.id)
    );
    
    // Mostrar mensaje de procesamiento para grandes cantidades
    if (selectedContacts.length > 50) {
      toast.info(i18n.t("contacts.messages.processingExport").replace('{count}', selectedContacts.length));
    }
    
    // Usar setTimeout para no bloquear la UI
    setTimeout(() => {
      try {
        // Crear el CSV con los datos filtrados
        const csvData = contactsToExport.map((contact) => ({ 
          name: contact.name || '', 
          number: contact.number || '', 
          email: contact.email || '' 
        }));
        
        // Crear el contenido CSV con encoding UTF-8 BOM para Excel
        const csvHeader = 'name;number;email\n';
        const csvContent = csvData.map(row => 
          `"${row.name.replace(/"/g, '""')}";"${row.number}";"${row.email}"`
        ).join('\n');
        
        const csvString = '\ufeff' + csvHeader + csvContent; // BOM para UTF-8
        
        // Crear y descargar el archivo
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `whaticket_${selectedContacts.length}_contacts_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar la URL del blob
        URL.revokeObjectURL(url);
        
        toast.success(i18n.t("contacts.messages.exportSuccess"));
      } catch (error) {
        console.error('Error al exportar:', error);
        toast.error(i18n.t("contacts.messages.exportError"));
      }
      
      setConfirmOpen(false);
    }, 100);
  };
  
  const handleCheckAgenda = async () => {
    try {
      toast.info(i18n.t("contacts.messages.checkingAgenda"));
      
      const { data: countData } = await getContactsCount();
      const count = countData.count;
      
      if (count === 0) {
        toast.warning(i18n.t("contacts.messages.agendaEmpty"));
        return;
      }
      
      setContactCount(count);
      setActionType("import");
      setConfirmOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleimportContact = async () => {
    try {
      setLoading(true);
      setConfirmOpen(false);
      
      if (!!fileUploadRef.current.files[0]) {
        const formData = new FormData();
        formData.append("file", fileUploadRef.current.files[0]);
        
        // ✅ CORREGIR: Capturar la respuesta del backend con mejor manejo de errores
        const response = await uploadContacts(formData);
        
        // ✅ CORREGIR: Validar que la respuesta existe y tiene la estructura esperada
        if (response && response.data) {
          if (response.data.success) {
            const { message } = response.data; // ✅ CORREGIR: Solo usar message, no importedContacts
            toast.success(`✅ ${message}`);
            
            // ✅ CORREGIDO: No hacer RESET porque los sockets ya actualizaron el estado en tiempo real
            // Los contactos ya están visibles gracias a las actualizaciones de socket
          } else {
            toast.error(response.data.message || i18n.t("contacts.messages.importErrorGeneric"));
          }
        } else {
          toast.error(i18n.t("contacts.messages.importInvalidResponse"));
        }
      } else {
        // ✅ Importar los contactos desde la agenda de WhatsApp
        const response = await importContacts();
        
        // ✅ Mostrar modal con resultados detallados (sin toasts)
        if (response && response.data) {
          setImportResult(response.data);
          setImportResultModalOpen(true);
        }
      }
      
      // ✅ Limpiar estados
      setContactCount(0);
      setActionType("");
      
      // ✅ CORREGIR: No usar history.go(0) que recarga toda la página
      // Los contactos se actualizarán automáticamente vía sockets
    } catch (err) {
      console.error("Error en importación de contactos:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // ✅ CORREGIR: Usar la URL completa del backend
      const backendUrl = window.WATOOLX_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const fileUrl = `${backendUrl}/files/plantilla-watoolx-contact.xlsx`;
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'plantilla-watoolx-contact.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(i18n.t("contacts.messages.templateDownloadSuccess"));
    } catch (error) {
      console.error('Error al descargar plantilla:', error);
      toast.error(i18n.t("contacts.messages.templateDownloadError"));
    }
  };
  
function getDateLastMessage(contact) {
    if (!contact) return null;
    if (!contact.tickets) return null;

    if (contact.tickets.length > 0) {
        const date = new Date(contact.tickets[contact.tickets.length - 1].updatedAt);

        const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
        const month = (date.getMonth() + 1) > 9 ? (date.getMonth() + 1) : `0${date.getMonth() + 1}`;
        const year = date.getFullYear().toString().slice(-2);

        const hours = date.getHours() > 9 ? date.getHours() : `0${date.getHours()}`;
        const minutes = date.getMinutes() > 9 ? date.getMinutes() : `0${date.getMinutes()}`;

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return null;
}

  return {
    actionType,
    confirmOpen,
    contactCount,
    contactModalOpen,
    contactTicket,
    contacts,
    deletingContact,
    dispatch,
    fileUploadRef,
    getDateLastMessage,
    hadleEditContact,
    handleCheckAgenda,
    handleCloseContactModal,
    handleCloseOrOpenTicket,
    handleCloseProfile360,
    handleDeleteContact,
    handleDeleteSelectedContacts,
    handleDownloadTemplate,
    handleExportSelectedContacts,
    handleOpenContactModal,
    handleSearch,
    handleSelectAll,
    handleSelectContact,
    handleimportContact,
    history,
    importResult,
    importResultModalOpen,
    loading,
    newTicketModalOpen,
    page,
    profile360Open,
    rowsPerPage,
    searchParam,
    selectAll,
    selectedContactId,
    selectedContacts,
    setActionType,
    setConfirmOpen,
    setContactCount,
    setContactModalOpen,
    setContactTicket,
    setDeletingContact,
    setImportResult,
    setImportResultModalOpen,
    setLoading,
    setNewTicketModalOpen,
    setPage,
    setProfile360Open,
    setRowsPerPage,
    setSearchParam,
    setSelectAll,
    setSelectedContactId,
    setSelectedContacts,
    setTotalCount,
    socketManager,
    totalCount,
    user,
    forceUpdate,
  };
}
